import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, ProgressBar, HealthPill, Button, DataTable, type Column, EmptyState, PhaseBadge } from "@/design/components";
import { Lock } from "lucide-react";
import { FunderInsightsDashboard } from "./FunderInsightsDashboard";
import { AuditTrail } from "@/screens/audit/AuditTrail";
import { DeliveryProgress } from "@/screens/programme/DeliveryProgress";
import { GitFork } from "lucide-react";
import { useSeed, useAuditEvents, useProgrammeSummaries, useMoney } from "@/domain/hooks";
import { useTrace } from "@/shell/TraceProvider";
import type { Disbursement } from "@/domain/types";
import { FunderApprovalDrawer } from "./FunderApprovalDrawer";

export function FunderProgrammesTab() {
  const programmes = useProgrammeSummaries();
  const money = useMoney();
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {programmes.map((p) => {
        const pending = p.funderVisibility === "not_shared";
        return (
          <Card
            key={p.id}
            className={pending ? "opacity-80" : "cursor-pointer transition-colors hover:border-strong"}
            onClick={() => !pending && p.workspaceId && navigate(`/workspace/${p.workspaceId}`)}
          >
            <CardBody className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-ink">{p.name}</p>
                  <p className="text-xs text-faint">Government of Uganda</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <PhaseBadge phase={p.phase} />
                  {!pending && <HealthPill rag={p.alertState} />}
                </div>
              </div>
              {pending ? (
                <p className="flex items-center gap-1.5 border-t border-hairline pt-3 text-sm text-faint">
                  <Lock className="h-3.5 w-3.5" aria-hidden /> Access pending: shared once the feasibility work is ready.
                </p>
              ) : (
                <>
                  <div>
                    <div className="flex items-center justify-between text-xs text-mute">
                      <span>Milestones disbursed</span>
                      <span className="tabular-nums">{p.milestonesDisbursed}</span>
                    </div>
                    <ProgressBar value={p.milestonesPct} className="mt-1.5" />
                  </div>
                  <div className="flex items-center justify-between border-t border-hairline pt-3 text-sm">
                    <span className="text-faint">Committed</span>
                    <span className="tabular-nums text-ink">{money(p.totalBudget)}</span>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}

export function FunderApprovalsTab() {
  const seed = useSeed();
  const money = useMoney();
  const { openTrace } = useTrace();
  const [open, setOpen] = useState<Disbursement | null>(null);
  const rows = seed.disbursements.filter((d) => d.state === "ready_for_funder" || d.state === "approved");
  const ms = (id: string) => seed.milestones.find((m) => m.id === id);
  // The project behind the disbursement currently in the queue, for the gate context.
  const queueProjectId = ms(rows[0]?.milestoneId ?? "")?.projectId;

  const columns: Column<Disbursement>[] = [
    {
      key: "milestone",
      header: "Milestone",
      cell: (d) => {
        const m = ms(d.milestoneId);
        return (
          <div>
            <p className="font-medium text-ink">{m?.name}</p>
            <p className="text-xs text-faint">{seed.projects.find((p) => p.id === m?.projectId)?.name}</p>
          </div>
        );
      },
    },
    { key: "amount", header: "Amount", align: "right", cell: (d) => <span className="tabular-nums text-ink">{money(d.amount)}</span> },
    { key: "rec", header: "Stratus recommendation", cell: (d) => <span className="line-clamp-2 max-w-md text-sm text-mute">{d.stratusRecommendation}</span> },
    { key: "age", header: "Age", cell: () => <span className="data text-xs text-faint">18h</span> },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (d) => (
        <div className="flex justify-end gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            aria-label="Trace source of this disbursement"
            onClick={() => {
              // Opening Trace closes Review — only one of the two panels is
              // ever open at once (Trace Source spec §3.1).
              setOpen(null);
              openTrace({ kind: "disbursement", id: d.id }, { from: "Funder approvals" });
            }}
          >
            <GitFork className="h-4 w-4" /> Trace source
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setOpen(d)}>Review</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      {queueProjectId && (
        <DeliveryProgress projectId={queueProjectId} showHeaderLink={false} showCta={false} />
      )}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(d) => d.id}
        onRowClick={setOpen}
        empty={<EmptyState title="No milestones awaiting approval" body="Disbursements routed by Stratus appear here for your approval. New routings arrive within minutes." />}
      />
      <FunderApprovalDrawer disbursement={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} />
    </div>
  );
}

export function FunderAnalyticsTab() {
  return <FunderInsightsDashboard />;
}

export function FunderAuditTab() {
  // Same event list as oversight, read-only (no CSV export) — matching demo
  // mode exactly. The narrower funder scoping in UI/UX spec v3 §1/§10.3 is
  // deliberately not applied here; it is tracked as separate work.
  return <AuditTrail events={useAuditEvents()} readonly />;
}
